import React, { useState, useEffect } from 'react'
import { Modal, Table, Badge, Button, InputGroup, Form, Spinner } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRedo, faDownload } from '@fortawesome/free-solid-svg-icons'
import { DebounceInput } from 'react-debounce-input'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal'
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary'
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert'
import * as RequisitionController from '../Controller/RequisitionController'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'

const RFQSendHistoryModal = ({ show, onHide, referenceId, storeId, props }) => {
  const [history, setHistory] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [resendingHistoryId, setResendingHistoryId] = useState(null)
  const [showResendConfirm, setShowResendConfirm] = useState(false)
  const [selectedHistorySlack, setSelectedHistorySlack] = useState(null)

  const fetchHistory = async () => {
    if (!referenceId) return
    try {
      const resp = await RequisitionController.getRfqSendHistoryApi(
        referenceId,
        'RFQ',
        searchTerm,
        currentPage,
        itemsPerPage,
        storeId || localStorage.getItem('store_id')
      )
      if (resp?.success) {
        setHistory(resp.data || [])
        setTotalItems(resp.total || 0)
      }
    } catch (error) {
      console.error('Error fetching RFQ history:', error)
    }
  }

  useEffect(() => {
    if (show && referenceId) fetchHistory()
  }, [show, referenceId, searchTerm, currentPage, itemsPerPage])

  const handleSearch = (e) => {
    setSearchTerm(e.target.value || '')
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleLimitChange = (limit) => {
    setItemsPerPage(limit)
    setCurrentPage(1)
  }

  const promptResendRFQ = (historySlack) => {
    setSelectedHistorySlack(historySlack)
    setShowResendConfirm(true)
  }

  const confirmResendRFQ = async () => {
    setShowResendConfirm(false)
    if (!selectedHistorySlack) return

    try {
      setResendingHistoryId(selectedHistorySlack)
      const resp = await RequisitionController.resendRfqNotificationApi(selectedHistorySlack)
      if (resp?.success) {
        Utility.toastNotifications('RFQ resent successfully', 'Success', 'success')
        fetchHistory()
      } else {
        Utility.toastNotifications(resp?.message || 'Failed to resend RFQ', 'Error', 'error')
      }
    } catch (error) {
      Utility.toastNotifications('Failed to resend RFQ', 'Error', 'error')
    } finally {
      setResendingHistoryId(null)
      setSelectedHistorySlack(null)
    }
  }

  const cancelResendRFQ = () => {
    setShowResendConfirm(false)
    setSelectedHistorySlack(null)
  }

  // Check permission for RFQ send history
  const requisitionPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'requisitions',
    ['send_rfq', 'send_notification'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  )

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="modal-90w"
      className='quotation-notification-history-modal'>
      <Modal.Header closeButton>
        <Modal.Title>RFQ Notification History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <InputGroup>
            <DebounceInput
              element={Form.Control}
              placeholder="Search by recipient, event, status..."
              value={searchTerm}
              onChange={handleSearch}
              debounceTimeout={500}
              minLength={2}
            />
          </InputGroup>
        </div>
        {history.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Event Type</th>
                  <th>Channel</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th>Delivered At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, idx) => (
                  <tr key={idx}>
                    <td>{h.event_type || '-'}</td>
                    <td>
                      <Badge bg="info">{h.channel || '-'}</Badge>
                    </td>
                    <td>
                      {h.recipient_address ? (
                        <div className="d-flex flex-wrap gap-2">
                          {h.recipient_address.split(',').map((r, i) => (
                            <Badge key={i} bg="light" text="dark" className="border">
                              {r.trim()}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <Badge bg={h.status === 'SENT' ? 'success' : 'danger'}>{h.status || '-'}</Badge>
                    </td>
                    <td>{Utility.get12HourFormat(h.sent_at) || '-'}</td>
                    <td>{Utility.get12HourFormat(h.delivered_at) || '-'}</td>
                    <td>
                      <div className="d-flex gap-2 align-items-center">
                        {requisitionPermissions.send_notification ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => promptResendRFQ(h.slack)}
                            disabled={resendingHistoryId === h.slack}
                            className="fw-medium"
                            style={{ fontSize: '0.8rem' }}
                            title="Resend RFQ"
                          >
                            {resendingHistoryId === h.slack ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faRedo} className="me-1" />
                                Resend
                              </>
                            )}
                          </Button>
                        ) : null}
                        {h.attachment_link ? (
                          <a
                            href={h.attachment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-info btn-sm"
                            title="Download Attachment"
                          >
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Download
                          </a>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-5">
            <div className="text-muted mb-2">
              <i className="fas fa-history fa-3x opacity-25"></i>
            </div>
            <p className="text-muted mb-0">No RFQ send history found</p>
          </div>
        )}
        {totalItems > 0 && (
          <div className="mt-3 pt-3 border-top">
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        )}
      </Modal.Body>
      <ErrorBoundary title="ModalGlobal Error">
        <ModalGlobal
          show={showResendConfirm}
          onHide={cancelResendRFQ}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Resend RFQ"
          footer={false}
          body={
            <>
              <ErrorBoundary title="ConfirmationAlert Error">
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to resend this RFQ notification?"
                  BodySecondContent=""
                  BodyThirdContent=""
                  confirmationButtonContent={resendingHistoryId ? 'Resending…' : 'Yes'}
                  cancelButtonContent="No"
                  deleteConfirmButton={confirmResendRFQ}
                  deleteCancleButton={cancelResendRFQ}
                />
              </ErrorBoundary>
            </>
          }
        />
      </ErrorBoundary>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType
})

export default connect(mapStateToProps)(withNamespaces()(RFQSendHistoryModal))
